import React, { useEffect, useState } from "react";
import {
  Search,
  RotateCw,
  SlidersHorizontal,
  Building2,
  User,
  CheckCircle2,
  Plus,
  Eraser,
  Check,
} from "lucide-react";
import Select from "react-select";
import { motion, AnimatePresence } from "framer-motion";
import { getAllBranches } from "../../../api/hrm/branches/info";
import { getCustomersByBranch } from "../../../api/crm/customers/customerInfo";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import MobileAdvancedFilterSheet from "../../../components/ui/MobileAdvancedFilterSheet";
import { hasPermission } from "../../../utils/permissionUtils";

const TEXT = {
  searchPlaceholder: "Tìm theo mã, tiêu đề hoặc đối tác...",
  refresh: "Làm mới",
  advanced: "Lọc nâng cao",
  advancedTitle: "Bộ lọc nâng cao",
  branch: "Chi nhánh",
  customer: "Khách hàng",
  status: "Trạng thái",
  allBranch: "Tất cả chi nhánh",
  allStatus: "Tất cả trạng thái",
  clear: "Xóa lọc",
  apply: "Áp dụng",
  addContract: "Soạn thảo hợp đồng",
};

const ContractFilter = ({ filters, setFilters, onRefresh, onNewClick }) => {
  const [advanced, setAdvanced] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [branchOptions, setBranchOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const canCreate = hasPermission("crm.contracts", "C");

  const statusOptions = [
    { value: "Đang thực hiện", label: "Đang thực hiện" },
    { value: "Hoàn thành", label: "Hoàn thành" },
    { value: "Chậm tiến độ", label: "Chậm tiến độ" },
    { value: "Sắp hết hạn", label: "Sắp hết hạn" },
  ];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    getAllBranches()
      .then((res) => {
        const data = Array.isArray(res?.items) ? res.items : [];
        setBranchOptions(
          data.map((b) => ({ value: b.id, label: b.name || "Không tên" }))
        );
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!filters.branch_id) {
      setCustomerOptions([]);
      return;
    }
    getCustomersByBranch(filters.branch_id)
      .then((res) => {
        const data = Array.isArray(res?.items) ? res.items : [];
        setCustomerOptions(
          data.map((c) => ({
            value: c.id,
            label: `${c.name} — ${c.ward || ""}`,
          }))
        );
      })
      .catch(console.error);
  }, [filters.branch_id]);

  const updateFilter = (name, value) => {
    setFilters((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "branch_id") next.customer_id = "";
      return next;
    });
  };

  const clearFilters = () => {
    setFilters({
      keyword: "",
      customer_id: "",
      branch_id: "",
      status: "",
      warning: null,
    });
  };

  const isFiltering = [filters.branch_id, filters.customer_id, filters.status].some(
    Boolean
  );
  const activeFilterCount = [
    filters.branch_id,
    filters.customer_id,
    filters.status,
  ].filter(Boolean).length;

  const selectStyles = {
    ...customSelectStyles,
    control: (provided, state) => ({
      ...(typeof customSelectStyles.control === "function"
        ? customSelectStyles.control(provided, state)
        : provided),
      minHeight: 48,
      borderRadius: 16,
      borderColor: state.isFocused ? "#ef4444" : "#e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 4px rgba(239, 68, 68, 0.05)" : "none",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  };

  const ActionButton = ({ icon: Icon, onClick, title, active = false }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all duration-200 ${
        active
          ? "bg-red-50 border-red-200 text-red-600 shadow-sm"
          : "bg-white border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500"
      }`}
    >
      <Icon size={18} />
    </button>
  );

  const SelectField = ({
    label,
    icon: Icon,
    options,
    value,
    onChange,
    placeholder,
    isDisabled = false,
  }) => (
    <div className="flex w-full flex-col gap-1.5">
      <label className="ml-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-gray-400">
        {Icon ? <Icon size={12} /> : null}
        {label}
      </label>
      <Select
        options={options}
        placeholder={placeholder}
        styles={selectStyles}
        value={options.find((x) => x.value === value) || null}
        onChange={(opt) => onChange(opt?.value || "")}
        isDisabled={isDisabled}
        isClearable
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        menuPosition="fixed"
      />
    </div>
  );

  const advancedFields = (
    <>
      <SelectField
        label={TEXT.branch}
        icon={Building2}
        options={branchOptions}
        value={filters.branch_id}
        onChange={(v) => updateFilter("branch_id", v)}
        placeholder={TEXT.allBranch}
      />
      <SelectField
        label={TEXT.customer}
        icon={User}
        options={customerOptions}
        value={filters.customer_id}
        onChange={(v) => updateFilter("customer_id", v)}
        placeholder={
          filters.branch_id ? "Tất cả khách hàng" : "Chọn chi nhánh trước"
        }
        isDisabled={!filters.branch_id}
      />
      <SelectField
        label={TEXT.status}
        icon={CheckCircle2}
        options={statusOptions}
        value={filters.status}
        onChange={(v) => updateFilter("status", v)}
        placeholder={TEXT.allStatus}
      />
    </>
  );

  /* ============================= MOBILE ============================= */
  if (isMobile) {
    return (
      <div className="mb-6 space-y-4">
        <div className="flex gap-2">
          <div className="relative flex-1 group">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
              size={18}
            />
            <input
              type="text"
              placeholder={TEXT.searchPlaceholder}
              value={filters.keyword || ""}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full rounded-2xl border border-gray-200 bg-white pl-11 pr-4 py-3 text-sm shadow-sm outline-none transition-all focus:border-red-500"
            />
          </div>
          <ActionButton icon={RotateCw} onClick={onRefresh} title={TEXT.refresh} />
          <button
            type="button"
            onClick={() => setAdvanced(true)}
            title={TEXT.advanced}
            className={`relative h-11 w-11 flex items-center justify-center rounded-xl border transition-all duration-200 ${
              activeFilterCount > 0
                ? "bg-red-50 border-red-200 text-red-600 shadow-sm"
                : "bg-white border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500"
            }`}
          >
            <SlidersHorizontal size={18} />
            {activeFilterCount > 0 ? (
              <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-700 px-1 text-[10px] font-bold text-white">
                {activeFilterCount}
              </span>
            ) : null}
          </button>
        </div>

        {canCreate && (
          <button
            type="button"
            onClick={onNewClick}
            className="fixed bottom-20 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-red-700 to-red-800 text-white shadow-lg shadow-red-200 active:scale-95"
            title={TEXT.addContract}
            aria-label={TEXT.addContract}
          >
            <Plus size={22} />
          </button>
        )}

        <MobileAdvancedFilterSheet
          open={advanced}
          onClose={() => setAdvanced(false)}
          title={TEXT.advancedTitle}
          footer={
            <>
              {isFiltering ? (
                <button
                  type="button"
                  onClick={() => {
                    clearFilters();
                    setAdvanced(false);
                  }}
                  className="flex-1 rounded-2xl border border-gray-300 py-4 font-bold text-gray-600 hover:bg-gray-50"
                >
                  {TEXT.clear}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setAdvanced(false)}
                className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-red-600 py-4 font-bold text-white shadow-lg shadow-red-200"
              >
                <Check size={18} />
                {TEXT.apply}
              </button>
            </>
          }
        >
          <div className="grid grid-cols-1 gap-4">{advancedFields}</div>
        </MobileAdvancedFilterSheet>
      </div>
    );
  }

  /* ============================= DESKTOP ============================= */
  return (
    <div className="mb-6 rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-1 items-center gap-3">
          <div className="relative w-full max-w-sm group">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
              size={18}
            />
            <input
              type="text"
              placeholder={TEXT.searchPlaceholder}
              value={filters.keyword || ""}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full rounded-2xl border-2 border-transparent bg-gray-50 pl-11 pr-4 py-2.5 font-medium text-gray-700 outline-none transition-all focus:border-red-500 focus:bg-white"
            />
          </div>

          <ActionButton icon={RotateCw} onClick={onRefresh} title={TEXT.refresh} />
          <ActionButton
            icon={SlidersHorizontal}
            onClick={() => setAdvanced(!advanced)}
            active={advanced}
            title={TEXT.advanced}
          />

          {isFiltering && (
            <button
              onClick={clearFilters}
              className="ml-2 flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-red-500 transition-all hover:text-red-700"
            >
              <Eraser size={14} /> {TEXT.clear}
            </button>
          )}
        </div>

        {canCreate && (
          <button
            onClick={onNewClick}
            className="flex items-center gap-2 rounded-full bg-gradient-to-r from-red-700 to-red-800 px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-all hover:shadow-lg hover:shadow-red-100 active:scale-95"
          >
            <Plus size={18} />
            {TEXT.addContract}
          </button>
        )}
      </div>

      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-6 grid grid-cols-1 gap-4 border-t border-gray-100 pt-6 md:grid-cols-2 lg:grid-cols-3">
              {advancedFields}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContractFilter;
